import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { YStack, XStack } from 'tamagui'

import { format } from 'date-fns-tz'

import { HOST_NAME } from '../../data/config/url'

import { Link } from '../Link'
import { H3, Text } from '../Text'
import { Card } from '../Card'
import { CoverPhotoWithBlurFill } from '../CoverPhoto/CoverPhotoWithBlurFill'
import { Icon } from '../Icon'
import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'
import { EventAttendButton } from './EventAttendButton'
import { EventAttendeesOverlap } from './EventAttendeesOverlap'

import { EventCardNew_event$key } from './__generated__/EventCardNew_event.graphql'
import { EventCardNew_query$key } from './__generated__/EventCardNew_query.graphql'

export const EventCard = Link.styleable<{
  event: EventCardNew_event$key
  query?: EventCardNew_query$key | null
  showOrganizer?: boolean
  showCoverPhoto?: boolean
  showAttendRow?: boolean
  titleNumberOfLines?: number
  href?: string
}>(
  (
    {
      event,
      query = null,
      showOrganizer = true,
      showCoverPhoto = true,
      showAttendRow = false,
      titleNumberOfLines = 2,
      ...props
    },
    ref
  ) => {
    const data = useFragment(
      graphql`
        fragment EventCardNew_event on Event
        @argumentDefinitions(
          includeAttendRow: { type: "Boolean!", defaultValue: false }
        ) {
          name
          prettyUrl
          startAt
          updatedAt

          hasVenue
          hasExternalUrl
          visibility

          coverPhoto {
            # Cover photo: 500px wide
            transformUrl(transform: { w: 500, dpr: 3 })
          }

          eventCardOwner: owner {
            __typename

            ... on Guild {
              name
              slugId

              primaryPhoto {
                rowId
              }

              ...GuildPrimaryPhotoNew_guild
            }

            ... on User {
              firstName
              lastName
              slugId

              ...UserPrimaryPhotoNew_user
            }
          }

          attendeesByRelevance: eventAttendeesByRelevance(
            filter: {
              status: {
                in: [
                  ATTENDING_IN_PERSON
                  ATTENDING_VIRTUAL
                  WAITING_IN_PERSON
                  WAITING_VIRTUAL
                ]
              }
            }
            first: 5
          ) @include(if: $includeAttendRow) {
            totalCount
            ...EventAttendeesOverlap_eventAttendees
          }

          attendingCount: eventAttendees(
            filter: {
              status: { in: [ATTENDING_IN_PERSON, ATTENDING_VIRTUAL] }
            }
          ) @include(if: $includeAttendRow) {
            totalCount
          }

          waitingCount: eventAttendees(
            filter: {
              status: { in: [WAITING_IN_PERSON, WAITING_VIRTUAL] }
            }
          ) @include(if: $includeAttendRow) {
            totalCount
          }

          ...EventAttendButtonNew_event @include(if: $includeAttendRow)
        }
      `,
      event
    )

    const queryData = useFragment(
      graphql`
        fragment EventCardNew_query on Query {
          ...EventAttendButtonNew_query
        }
      `,
      query
    )

    const startAt = useMemo(() => {
      try {
        const date = new Date(data.startAt)
        if (date.getMinutes() === 0) {
          return format(date, 'EEE, MMM do · haa')
        }
        return format(date, 'EEE, MMM do · h:mmaa')
      } catch {
        return ''
      }
    }, [data.startAt])

    const coverPhotoSrc = useMemo(() => {
      if (data.coverPhoto?.transformUrl) {
        return data.coverPhoto.transformUrl
      }
      return `${HOST_NAME}/social/event/${data.prettyUrl}/card.svg?${data.updatedAt}`
    }, [data.coverPhoto, data.prettyUrl, data.updatedAt])

    const owner = useMemo(() => {
      if (!showOrganizer) return null

      switch (data.eventCardOwner?.__typename) {
        case 'Guild':
          return (
            <XStack gap="$3" alignItems="center">
              {data.eventCardOwner.primaryPhoto ? (
                <GuildPrimaryPhoto guild={data.eventCardOwner} size="$7" />
              ) : (
                <YStack width="$7" alignItems="center">
                  <Icon name="people" size="$6" color="$color" />
                </YStack>
              )}
              <Text size="$4" numberOfLines={1}>
                {data.eventCardOwner.name}
              </Text>
            </XStack>
          )
        case 'User':
          return (
            <XStack gap="$3" alignItems="center">
              <UserPrimaryPhoto user={data.eventCardOwner} size="$7" />
              <Text size="$4" numberOfLines={1}>
                {data.eventCardOwner.firstName} {data.eventCardOwner.lastName}
              </Text>
            </XStack>
          )
        default:
          return null
      }
    }, [data.eventCardOwner, showOrganizer])

    const hasAttendees = showAttendRow && data.attendeesByRelevance?.totalCount

    return (
      <Link
        {...props}
        ref={ref}
        href={`/events/${data.prettyUrl}`}
        aria-label={`Link to ${data.name} Event`}
        display="flex"
        flexDirection="column"
        hoverStyle={{ textDecorationLine: 'none' }}
      >
        {showCoverPhoto && (
          <CoverPhotoWithBlurFill
            alt={`Cover Photo for ${data.name}`}
            src={coverPhotoSrc}
            maxHeight={200}
            borderTopLeftRadius="$6"
            borderTopRightRadius="$6"
          />
        )}

        <Card
          gap="$4"
          padding="$4"
          borderRadius="$6"
          borderTopLeftRadius={showCoverPhoto ? 0 : undefined}
          borderTopRightRadius={showCoverPhoto ? 0 : undefined}
          flex={props.flex}
          style={{
            willChange: 'box-shadow',
            transition: 'all 375ms cubic-bezier(0,0.5,1,1)',
          }}
          hoverStyle={{
            // @ts-expect-error boxShadow is web-only
            boxShadow:
              'rgba(10, 37, 64, 0.16) 0px 8px 16px -4px, rgba(10, 37, 64, 0.12) 0px 10px 30px -7px',
          }}
        >
          <YStack gap="$2">
            <XStack gap="$2" alignItems="center">
              <Text color="$colorMuted" fontWeight="$6" size="$4">
                {startAt}
              </Text>

              <JoiningBadge
                hasVenue={data.hasVenue}
                hasExternalUrl={data.hasExternalUrl}
              />
            </XStack>

            {data.visibility === 'UNLISTED' && (
              <XStack>
                <Text
                  color="$primaryColor"
                  fontWeight="$6"
                  size="$3"
                  backgroundColor="$backgroundAccentMuted"
                  paddingVertical="$1"
                  paddingHorizontal="$2"
                  borderRadius="$2"
                >
                  Unlisted
                </Text>
              </XStack>
            )}
          </YStack>

          <H3 size="$6" numberOfLines={titleNumberOfLines}>
            {data.name}
          </H3>

          {owner}

          {showAttendRow && (
            <XStack gap="$3" alignItems="center">
              {hasAttendees ? (
                <EventAttendeesOverlap
                  eventAttendees={data.attendeesByRelevance}
                  attendingCount={data.attendingCount?.totalCount ?? 0}
                  waitingCount={data.waitingCount?.totalCount ?? 0}
                  size="$5"
                  flex={1}
                />
              ) : (
                <XStack flex={1} />
              )}

              {queryData && (
                <EventAttendButton
                  event={data}
                  query={queryData}
                  height={32}
                  minHeight={32}
                  borderRadius={16}
                />
              )}
            </XStack>
          )}
        </Card>
      </Link>
    )
  }
)

function JoiningBadge({
  hasVenue,
  hasExternalUrl,
}: {
  hasVenue: boolean | null | undefined
  hasExternalUrl: boolean | null | undefined
}) {
  if (hasVenue && hasExternalUrl) {
    return (
      <Text color="$colorMuted" fontWeight="$5" size="$4">
        In-Person & Online
      </Text>
    )
  }
  if (hasVenue) {
    return (
      <Text color="$colorMuted" fontWeight="$5" size="$4">
        In-Person
      </Text>
    )
  }
  if (hasExternalUrl) {
    return (
      <Text color="$colorMuted" fontWeight="$5" size="$4">
        Online
      </Text>
    )
  }
  return null
}
